import { useEffect, useMemo, useState, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { DataGrid, KeenIcon } from '@/components';
import usePagination from '@/hooks/usePagination';
import {
  useGetPoiRequestsQuery,
  useUpdatePoiRequestMutation,
  useApproveAndCreatePoiMutation,
  useUpdatePoiRequestStatusMutation,
} from '@/services/PoiRequestManagement';
import {
  IPoiRequest,
  PoiRequestUpdateData,
} from '@/types/poi-request-management.types';
import { IPOIData } from '@/types/poi-management.types';
import { useGetPOICategoriesQuery, useTranslatePOIContentMutation } from '@/services/POIManagementService';
import { PoiRequestEditModal } from './PoiRequestEditModal';
import PoiModal from '@/pages/poi-management/POIModal';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { useLanguage, I18N_LANGUAGES } from '@/i18n';
import { usePermissions } from '@/hooks';

const formatCategoryDisplay = (category: string | null | undefined): string => {
  if (!category || typeof category !== 'string') return 'N/A';
  return category
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const getCategoryNameById = (
  categoryId: string | null | undefined,
  categories: Array<{ id: string; label?: string; name?: string }>
): string => {
  if (!categoryId || !Array.isArray(categories)) return 'N/A';
  const category = categories.find((cat) => cat && cat.id === categoryId);
  return category ? category.label || category.name || 'N/A' : 'N/A';
};

const getStatusBadge = (status: string) => {
  const statusLower = status?.toLowerCase();
  switch (statusLower) {
    case 'pending':
      return <span className="badge badge-info">Pending</span>;
    case 'approved':
      return <span className="badge badge-success">Approved</span>;
    case 'rejected':
      return <span className="badge badge-danger">Rejected</span>;
    default:
      return (
        <span className="badge badge-secondary">
          {formatCategoryDisplay(status)}
        </span>
      );
  }
};

const PoiRequestListings = ({ harborId }: { harborId: string | null }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();
  const { currentLanguage } = useLanguage();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedPoiRequest, setSelectedPoiRequest] =
    useState<IPoiRequest | null>(null);
  const [formData, setFormData] = useState<PoiRequestUpdateData>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Approve modal state
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [approvePoiRequest, setApprovePoiRequest] =
    useState<IPoiRequest | null>(null);
  const [approveFormData, setApproveFormData] = useState<Partial<IPOIData>>({
    name: '',
    description: '',
    category_id: '',
    subtype_id: '',
    latitude: 0,
    longitude: 0,
    tags: [],
    images: [],
    harborId: '',
    address: '',
    contact_info: '',
    entry_fee: 0,
    opening_hours: '',
    accessibility_notes: '',
    status: 'Active',
    media_captions: [],
  });
  const [approveErrors, setApproveErrors] = useState<Record<string, string>>(
    {}
  );
  const [translations, setTranslations] = useState<
    Record<string, Partial<IPOIData>>
  >({});
  const [isTranslating, setIsTranslating] = useState(false);

  // Categories for POI modal (approve functionality)
  const { data: categoriesResponse, refetch: refetchCategories } =
    useGetPOICategoriesQuery();
  const categories = useMemo(
    () => categoriesResponse || [],
    [categoriesResponse]
  );

  // Reject confirmation modal state
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectPoiRequest, setRejectPoiRequest] = useState<IPoiRequest | null>(
    null
  );

  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
    resetSearch,
  } = usePagination();

  const [updatePoiRequest, { isLoading: isUpdating }] =
    useUpdatePoiRequestMutation();
  const [approveAndCreatePoi, { isLoading: isApproving }] =
    useApproveAndCreatePoiMutation();
  const [updatePoiRequestStatus, { isLoading: isRejecting }] =
    useUpdatePoiRequestStatusMutation();
  const [translatePOIContent] = useTranslatePOIContentMutation();

  const queryParams = useMemo(
    () => ({
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      search: debouncedValue,
      harborId: harborId || undefined,
    }),
    [pagination.pageIndex, pagination.pageSize, debouncedValue, harborId]
  );

  // Don't fetch if harborId is not provided

  const {
    data,
    isLoading,
    isFetching,
    isError,
    refetch: refetchPoiRequests,
  } = useGetPoiRequestsQuery(queryParams);

  useEffect(() => {
    if (typeof refetchPoiRequests === 'function') {
      refetchPoiRequests();
    }
  }, [debouncedValue, refetchPoiRequests]);

  useEffect(() => {
    if (data) {
      handleTotalPages(data.totalPages || 1);
      handleTotalItems(data.totalItems || 0);
    }
  }, [data, handleTotalPages, handleTotalItems]);

  useEffect(() => {
    refetchCategories();
  }, [currentLanguage.code, refetchCategories]);

  const rows: IPoiRequest[] = useMemo(() => {
    if (!data?.data) return [];
    return data.data;
  }, [data]);

  const handleOpenEditModal = useCallback((poiRequest: IPoiRequest) => {
    setSelectedPoiRequest(poiRequest);
    setIsEditModalOpen(true);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setIsEditModalOpen(false);
    setSelectedPoiRequest(null);
    setFormData({});
    setErrors({});
  }, []);

  const handleOpenApproveModal = useCallback((poiRequest: IPoiRequest) => {
    setApprovePoiRequest(poiRequest);
    // Pre-populate form data from POI request
    setApproveFormData({
      name: poiRequest.name || '',
      description: poiRequest.description || '',
      category: poiRequest.category || '',
      category_id: poiRequest.categoryId || '',
      subtype_id: '',
      latitude: poiRequest.latitude || 0,
      longitude: poiRequest.longitude || 0,
      tags: poiRequest.tags || [],
      images: [],
      harborId: poiRequest.harborId || '',
      address: poiRequest.address || '',
      contact_info: poiRequest.contact_info || '',
      entry_fee: poiRequest.entry_fee || 0,
      opening_hours: poiRequest.opening_hours || '',
      accessibility_notes: poiRequest.accessibility_notes || '',
      status: 'Active',
      media_captions:
        poiRequest.media_captions && poiRequest.media_captions.length > 0
          ? poiRequest.media_captions
          : poiRequest.image_captions?.map((caption) => ({
              image_index: caption.image_index,
              captions: caption.translations,
            })) || [],
    });
    setApproveErrors({});
    setIsApproveModalOpen(true);
  }, []);

  const handleCloseApproveModal = useCallback(() => {
    setIsApproveModalOpen(false);
    setApprovePoiRequest(null);
    setApproveFormData({
      name: '',
      description: '',
      category: '',
      category_id: '',
      subtype_id: '',
      latitude: 0,
      longitude: 0,
      tags: [],
      images: [],
      harborId: '',
      address: '',
      contact_info: '',
      entry_fee: 0,
      opening_hours: '',
      accessibility_notes: '',
      status: 'Active',
    });
    setApproveErrors({});
    setTranslations({});
  }, []);

  const handleOpenRejectModal = useCallback((poiRequest: IPoiRequest) => {
    setRejectPoiRequest(poiRequest);
    setIsRejectModalOpen(true);
  }, []);

  const handleCloseRejectModal = useCallback(() => {
    setIsRejectModalOpen(false);
    setRejectPoiRequest(null);
  }, []);

  const handleRejectConfirm = useCallback(
    async (reason?: string) => {
      if (!rejectPoiRequest?.id) return;

      try {
        await updatePoiRequestStatus({
          id: rejectPoiRequest.id,
          data: {
            status: 'rejected',
            reason: reason,
          },
          harborId,
        }).unwrap();

        enqueueSnackbar('POI request rejected successfully', {
          variant: 'success',
        });
        handleCloseRejectModal();
        refetchPoiRequests();
      } catch (error) {
        let errorMessage = 'Failed to reject POI request';

        if (error && typeof error === 'object') {
          const rtkError = error as { data?: unknown; message?: string };

          if (
            rtkError.data &&
            typeof rtkError.data === 'object' &&
            rtkError.data !== null
          ) {
            const errorData = rtkError.data as {
              data?: { message?: string };
              message?: string;
            };

            if (
              errorData.data &&
              typeof errorData.data === 'object' &&
              'message' in errorData.data &&
              typeof errorData.data.message === 'string'
            ) {
              errorMessage = errorData.data.message;
            } else if (
              'message' in errorData &&
              typeof errorData.message === 'string'
            ) {
              errorMessage = errorData.message;
            }
          } else if (
            'message' in rtkError &&
            typeof rtkError.message === 'string'
          ) {
            errorMessage = rtkError.message;
          }
        }

        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    },
    [
      rejectPoiRequest,
      updatePoiRequestStatus,
      enqueueSnackbar,
      handleCloseRejectModal,
      refetchPoiRequests,
      harborId,
    ]
  );

  const handleSubmitEdit = useCallback(
    async (data: PoiRequestUpdateData) => {
      if (!selectedPoiRequest) return;

      // Basic validation
      const newErrors: Record<string, string> = {};
      if (!data.name?.trim()) {
        newErrors.name = 'Name is required';
      }
      if (!data.category_id) {
        newErrors.category_id = 'Category is required';
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }

      try {
        // Convert category_id to categoryId for API payload
        const apiPayload = {
          ...data,
          categoryId: data.category_id,
          category_id: undefined, // Remove category_id from payload
        };

        await updatePoiRequest({
          id: selectedPoiRequest.id,
          data: apiPayload,
          harborId,
        }).unwrap();

        enqueueSnackbar('POI request updated successfully', {
          variant: 'success',
        });
        handleCloseEditModal();
        refetchPoiRequests();
      } catch (error) {
        let errorMessage = 'Failed to update POI request';

        if (error && typeof error === 'object') {
          // RTK Query error structure: { status: number, data: ErrorApiResponse | unknown }
          const rtkError = error as { data?: unknown; message?: string };

          if (
            rtkError.data &&
            typeof rtkError.data === 'object' &&
            rtkError.data !== null
          ) {
            // Check if error.data is ErrorApiResponse (has nested data.message)
            const errorData = rtkError.data as {
              data?: { message?: string };
              message?: string;
            };

            if (
              errorData.data &&
              typeof errorData.data === 'object' &&
              'message' in errorData.data &&
              typeof errorData.data.message === 'string'
            ) {
              // ErrorApiResponse structure: error.data.data.message
              errorMessage = errorData.data.message;
            } else if (
              'message' in errorData &&
              typeof errorData.message === 'string'
            ) {
              // Direct message in error.data.message
              errorMessage = errorData.message;
            }
          } else if (
            'message' in rtkError &&
            typeof rtkError.message === 'string'
          ) {
            // Direct message in error.message
            errorMessage = rtkError.message;
          }
        }

        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    },
    [
      selectedPoiRequest,
      updatePoiRequest,
      enqueueSnackbar,
      handleCloseEditModal,
      refetchPoiRequests,
      harborId,
    ]
  );

  const handleApproveSubmit = useCallback(
    async (submittedTranslations?: Record<string, Partial<IPOIData>>) => {
      if (!approvePoiRequest?.id) return;

      try {
        let translationsToUse = submittedTranslations || translations;

        // If translations are not available, fetch them from the translation API first
        if (!translationsToUse || Object.keys(translationsToUse).length === 0) {
          setIsTranslating(true);
          try {
            const dataToTranslate = {
              name: approveFormData.name,
              category_id: approveFormData.category_id
                ? String(approveFormData.category_id)
                : undefined,
              description: approveFormData.description,
              tags: approveFormData.tags,
              source_language: currentLanguage.code || 'en',
            };

            const response = await translatePOIContent(dataToTranslate).unwrap();

            if (response && response.translations) {
              let finalTranslations = response.translations;

              if (response.warning_message) {
                enqueueSnackbar(response.warning_message, {
                  variant: 'warning',
                });

                const sourceLang = currentLanguage.code || 'en';
                const sourceTranslation =
                  response.translations[sourceLang] ||
                  response.translations[Object.keys(response.translations)[0]];

                finalTranslations = { ...response.translations };
                I18N_LANGUAGES.forEach((lang) => {
                  if (!finalTranslations[lang.code]) {
                    finalTranslations[lang.code] = {
                      name: sourceTranslation?.name || approveFormData.name || '',
                      description:
                        sourceTranslation?.description ||
                        approveFormData.description ||
                        '',
                      category:
                        sourceTranslation?.category || approveFormData.category || '',
                      tags: sourceTranslation?.tags
                        ? [...sourceTranslation.tags]
                        : approveFormData.tags
                          ? [...approveFormData.tags]
                          : [],
                    };
                  }
                });
              }

              translationsToUse = finalTranslations;
            } else {
              throw new Error('No translations returned');
            }
          } catch (translationError) {
            console.error('Translation failed during POI approval, using fallback:', translationError);
            // On error of translation, send the source in all languages for translation object
            const fallbackTranslations: Record<string, Partial<IPOIData>> = {};
            I18N_LANGUAGES.forEach((lang) => {
              fallbackTranslations[lang.code] = {
                name: approveFormData.name || '',
                description: approveFormData.description || '',
                category: approveFormData.category || '',
                tags: approveFormData.tags ? [...approveFormData.tags] : [],
              };
            });
            translationsToUse = fallbackTranslations;
          } finally {
            setIsTranslating(false);
          }
        }

        // Create FormData from approveFormData
        const formDataToSend = new FormData();
        formDataToSend.append('requestId', approvePoiRequest.id);
        formDataToSend.append('name', approveFormData.name || '');
        formDataToSend.append('description', approveFormData.description || '');
        if (approveFormData.category_id) {
          formDataToSend.append(
            'category_id',
            String(approveFormData.category_id)
          );
        }
        if (approveFormData.subtype_id) {
          formDataToSend.append(
            'subtype_id',
            String(approveFormData.subtype_id)
          );
        }
        formDataToSend.append(
          'latitude',
          String(approveFormData.latitude || 0)
        );
        formDataToSend.append(
          'longitude',
          String(approveFormData.longitude || 0)
        );
        formDataToSend.append('harborId', approveFormData.harborId || '');
        if (approveFormData.address) {
          formDataToSend.append('address', approveFormData.address);
        }
        if (approveFormData.contact_info) {
          formDataToSend.append('contact_info', approveFormData.contact_info);
        }
        if (approveFormData.entry_fee !== undefined) {
          formDataToSend.append('entry_fee', String(approveFormData.entry_fee));
        }
        if (approveFormData.opening_hours) {
          formDataToSend.append('opening_hours', approveFormData.opening_hours);
        }
        if (approveFormData.accessibility_notes) {
          formDataToSend.append(
            'accessibility_notes',
            approveFormData.accessibility_notes
          );
        }
        if (approveFormData.tags) {
          approveFormData.tags.forEach((tag) => {
            formDataToSend.append('tags[]', tag);
          });
        }
        if (approveFormData.images) {
          approveFormData.images.slice(0, 5).forEach((image) => {
            if (image instanceof File) {
              formDataToSend.append('mediaFiles[]', image);
            } else {
              formDataToSend.append('images[]', image);
            }
          });
        }
        if (approveFormData.status) {
          formDataToSend.append('status', approveFormData.status);
        }

        // Add translations if available
        if (translationsToUse && Object.keys(translationsToUse).length > 0) {
          formDataToSend.append('translations', JSON.stringify(translationsToUse));
        }

        if (
          approveFormData.media_captions &&
          approveFormData.media_captions.length > 0
        ) {
          formDataToSend.append(
            'image_captions',
            JSON.stringify(approveFormData.media_captions)
          );
        }

        await approveAndCreatePoi({
          id: approvePoiRequest.id,
          data: formDataToSend,
          harborId,
        }).unwrap();

        enqueueSnackbar('POI request approved and POI created successfully', {
          variant: 'success',
        });
        handleCloseApproveModal();
        refetchPoiRequests();
      } catch (error) {
        let errorMessage = 'Failed to approve POI request and create POI';

        if (error && typeof error === 'object') {
          const rtkError = error as { data?: unknown; message?: string };

          if (
            rtkError.data &&
            typeof rtkError.data === 'object' &&
            rtkError.data !== null
          ) {
            const errorData = rtkError.data as {
              data?: { message?: string };
              message?: string;
            };

            if (
              errorData.data &&
              typeof errorData.data === 'object' &&
              'message' in errorData.data &&
              typeof errorData.data.message === 'string'
            ) {
              errorMessage = errorData.data.message;
            } else if (
              'message' in errorData &&
              typeof errorData.message === 'string'
            ) {
              errorMessage = errorData.message;
            }
          } else if (
            'message' in rtkError &&
            typeof rtkError.message === 'string'
          ) {
            errorMessage = rtkError.message;
          }
        }

        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    },
    [
      approvePoiRequest,
      approveAndCreatePoi,
      approveFormData,
      enqueueSnackbar,
      handleCloseApproveModal,
      refetchPoiRequests,
      translations,
      currentLanguage.code,
      translatePOIContent,
      setIsTranslating,
      harborId,
    ]
  );

  const hasPendingRequests = useMemo(
    () => rows.some((request) => request.status?.toLowerCase() === 'pending'),
    [rows]
  );

  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessor: (row: IPoiRequest) => row.name,
        id: 'name',
        header: 'Name',
        enableSorting: false,
        cell: ({ row }: { row: { original: IPoiRequest } }) => (
          <span
            className="font-medium block"
            style={{
              minWidth: 0,
              maxWidth: '200px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={row.original.name}
          >
            {row.original.name}
          </span>
        ),
      },
      {
        accessor: (row: IPoiRequest) => row.categoryId || row.category,
        id: 'category',
        header: 'Category',
        enableSorting: false,
        cell: ({ row }: { row: { original: IPoiRequest } }) => {
          const categoryName = row.original.categoryId
            ? getCategoryNameById(row.original.categoryId, categories)
            : formatCategoryDisplay(row.original.category);

          // Debug fallback - show categoryId if name lookup fails
          const displayText =
            categoryName === 'N/A' && row.original.categoryId
              ? `${row.original.categoryId} (ID)`
              : categoryName;

          return (
            <span
              className="text-gray-800 font-normal"
              title={row.original.categoryId || undefined}
            >
              {displayText}
            </span>
          );
        },
      },
      {
        accessor: (row: IPoiRequest) => row.description,
        id: 'description',
        header: 'Description',
        enableSorting: false,
        cell: ({ row }: { row: { original: IPoiRequest } }) => (
          <span
            className="text-gray-800 font-normal block"
            style={{
              minWidth: 0,
              maxWidth: '300px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={row.original.description}
          >
            {row.original.description || '-'}
          </span>
        ),
      },
      {
        accessor: (row: IPoiRequest) => row.status,
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }: { row: { original: IPoiRequest } }) =>
          getStatusBadge(row.original.status),
      },
    ];

    if (hasPendingRequests) {
      baseColumns.push({
        accessor: (row: IPoiRequest) => row.id,
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }: { row: { original: IPoiRequest } }) => {
          if (row.original.status?.toLowerCase() !== 'pending') {
            return <></>;
          }

          return (
            <div className="flex gap-2">
              {hasPermission('poi_request_update') && (
                <button
                  className="btn btn-sm btn-icon btn-light"
                  onClick={() => handleOpenEditModal(row.original)}
                  title="Edit"
                >
                  <KeenIcon icon="pencil" className="text-base" />
                </button>
              )}
              {hasPermission('poi_request_approve') && (
                <button
                  className="btn btn-sm btn-icon btn-light btn-outline"
                  onClick={() => handleOpenApproveModal(row.original)}
                  title="Approve"
                  aria-label={`Approve POI request ${row.original.name}`}
                >
                  <KeenIcon icon="check" className="text-base" />
                </button>
              )}
              {hasPermission('poi_request_approve') && (
                <button
                  className="btn btn-sm btn-icon btn-light btn-outline"
                  onClick={() => handleOpenRejectModal(row.original)}
                  title="Reject"
                  aria-label={`Reject POI request ${row.original.name}`}
                >
                  <KeenIcon type="filled" icon="cross" />
                </button>
              )}
            </div>
          );
        },
      });
    }

    return baseColumns;
  }, [
    handleOpenEditModal,
    handleOpenApproveModal,
    hasPendingRequests,
    handleOpenRejectModal,
    categories,
    hasPermission,
  ]);

  const resetFilters = () => {
    if (searchTerm) {
      resetSearch();
    }
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  return (
    <>
      <div className="card card-grid w-full">
        <div className="card-header flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between">
          <div className="flex flex-wrap items-center gap-2 sm:gap-4 w-full justify-between">
            <div className="flex items-center gap-2">
              <div className="flex-shrink-0" role="search">
                <label
                  className="input input-sm h-8 sm:h-10 flex items-center gap-2"
                  htmlFor="search-poi-requests"
                  aria-label="Search POI requests"
                >
                  <KeenIcon
                    type="filled"
                    icon="magnifier"
                    className="text-gray-500"
                    aria-hidden="true"
                  />
                  <input
                    id="search-poi-requests"
                    type="text"
                    placeholder="Search POI requests..."
                    className="w-full bg-transparent border-0 outline-none focus:ring-0"
                    value={searchTerm}
                    onChange={handleSearch}
                    aria-label="Search POI requests"
                  />
                </label>
              </div>
              {searchTerm && (
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  onClick={resetFilters}
                  aria-label="Reset filters"
                >
                  Reset
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="card-body">
          {isLoading || isFetching ? (
            <div
              className="text-center py-4"
              role="status"
              aria-live="polite"
              aria-busy="true"
            >
              Loading POI requests...
            </div>
          ) : isError ? (
            <div
              className="text-center py-4 text-red-500"
              role="alert"
              aria-live="assertive"
            >
              Failed to load POI requests. Please try again.
            </div>
          ) : (
            <div className="poi-request-grid-wrapper">
              <DataGrid<IPoiRequest>
                columns={columns}
                data={rows}
                loading={isLoading || isFetching}
                pagination={{
                  page: pagination.pageIndex,
                  size: pagination.pageSize,
                  more: true,
                  totalItems,
                  totalPages,
                  setPagination,
                  info: `{from} - {to} of {count}`,
                  sizes: [5, 10, 25, 50, 100],
                  sizesLabel: 'Show',
                  sizesDescription: 'per page',
                }}
                serverSide={true}
                onFilterReset={resetFilters}
                hasActiveFilters={!!searchTerm}
                aria-label="POI request listings table"
              />
            </div>
          )}
        </div>
      </div>

      <PoiRequestEditModal
        open={isEditModalOpen}
        onClose={handleCloseEditModal}
        poiRequest={selectedPoiRequest}
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        setErrors={setErrors}
        onSubmit={handleSubmitEdit}
        isSubmitting={isUpdating}
      />

      <PoiModal
        mode="create"
        formData={approveFormData}
        setFormData={setApproveFormData}
        errors={approveErrors}
        setErrors={setApproveErrors}
        onSubmit={handleApproveSubmit}
        isSubmitting={isApproving || isTranslating}
        open={isApproveModalOpen}
        onClose={handleCloseApproveModal}
        categories={categories}
        onTranslationsGenerated={setTranslations}
      />

      <ConfirmationModal
        open={isRejectModalOpen}
        onClose={handleCloseRejectModal}
        title="Reject POI Request"
        message={`Are you sure you want to reject the POI request "${rejectPoiRequest?.name || ''}"?`}
        onConfirm={handleRejectConfirm}
        confirmLabel={isRejecting ? 'Rejecting...' : 'Reject'}
        cancelLabel="Cancel"
        showReasonInput={true}
        confirmButtonVariant="danger"
        disabled={isRejecting}
      />
    </>
  );
};

export { PoiRequestListings };
